(() => {
  const root = document.querySelector('[data-destiny-matrix]');
  if (!root) return;

  const meanings = {
    1: ['The Magician', 'Initiative & focus', 'Turns intention into a first practical move. In balance, this energy uses skill with purpose rather than forcing an outcome.'],
    2: ['The High Priestess', 'Intuition & inner knowing', 'Notices what is quiet, subtle, or still forming. It invites patience without losing contact with your own discernment.'],
    3: ['The Empress', 'Creation & abundance', 'Grows what receives steady care. Its strength is generous expression; its lesson is to create without overextending yourself.'],
    4: ['The Emperor', 'Structure & responsibility', 'Builds boundaries, systems, and lasting foundations. It works best when leadership remains flexible enough to listen.'],
    5: ['The Hierophant', 'Learning & values', 'Connects experience with meaning, teaching, and tradition. It asks you to choose principles consciously instead of following them automatically.'],
    6: ['The Lovers', 'Choice & connection', 'Brings relationships and values into focus. It asks for sincere choices, mutuality, and commitments you can genuinely live by.'],
    7: ['The Chariot', 'Direction & will', 'Gathers energy around a chosen direction. Progress becomes sustainable when determination and emotional self-command work together.'],
    8: ['Justice', 'Balance & accountability', 'Clarifies cause, consequence, and fair exchange. Its power is honest evaluation rather than perfection or harsh self-judgment.'],
    9: ['The Hermit', 'Wisdom & reflection', 'Creates space for insight that cannot be rushed. Solitude is useful here when it supports clarity rather than disconnection.'],
    10: ['Wheel of Fortune', 'Cycles & movement', 'Marks a turning point or change of rhythm. Adaptability matters more than trying to control every variable.'],
    11: ['Strength', 'Courage & self-trust', 'Works through calm inner authority rather than pressure. It asks you to meet intensity with patience, warmth, and steady courage.'],
    12: ['The Hanged One', 'Pause & perspective', 'Changes the view before changing the situation. A conscious pause can reveal options that urgency keeps hidden.'],
    13: ['Transformation', 'Endings & renewal', 'Releases a form that has completed its purpose. This energy supports honest transition, not fear-based destruction.'],
    14: ['Temperance', 'Integration & rhythm', 'Blends different needs into a workable whole. Its gift is measured progress and the ability to find a sustainable pace.'],
    15: ['The Devil', 'Desire & attachment', 'Makes motivations, appetites, and power dynamics visible. Awareness restores choice where habit once felt automatic.'],
    16: ['The Tower', 'Truth & restructuring', 'Reveals what can no longer be supported as it is. The constructive task is rebuilding from what remains true.'],
    17: ['The Star', 'Hope & authenticity', 'Renews direction through honesty and a longer view. Its light grows through consistent trust, not passive wishing.'],
    18: ['The Moon', 'Sensitivity & imagination', 'Moves through dreams, emotion, and uncertainty. Grounding helps intuition become useful instead of being confused with fear.'],
    19: ['The Sun', 'Vitality & visibility', 'Supports warmth, confidence, and clear expression. Its healthiest form makes room for others to shine as well.'],
    20: ['Judgement', 'Awakening & vocation', 'Calls for an honest response to what you now understand. It turns reflection into a more deliberate way of living.'],
    21: ['The World', 'Completion & belonging', 'Integrates experience into a fuller whole. Completion here is not a stop; it is readiness for the next wider cycle.'],
    22: ['The Fool', 'Freedom & beginnings', 'Opens space for discovery and a new path. Curiosity becomes powerful when it travels with awareness and responsibility.']
  };

  const positions = {
    north: [500, 92, 56, 'Your birth month'],
    north_east: [790, 210, 53, 'Upper-right foundation'],
    east: [908, 500, 56, 'Your birth year'],
    south_east: [790, 790, 53, 'Lower-right foundation'],
    south: [500, 908, 56, 'Your lower foundation'],
    south_west: [210, 790, 53, 'Lower-left foundation'],
    west: [92, 500, 56, 'Your birth day'],
    north_west: [210, 210, 53, 'Upper-left foundation'],
    center: [500, 500, 68, 'Your center'],
    north_outer: [500, 174, 41, 'Upper outer layer'],
    north_inner: [500, 244, 39, 'Upper inner layer'],
    north_core: [500, 332, 37, 'Upper core'],
    east_outer: [826, 500, 41, 'Right outer layer'],
    east_inner: [756, 500, 39, 'Right inner layer'],
    east_core: [668, 500, 37, 'Right core'],
    south_outer: [500, 826, 41, 'Lower outer layer'],
    south_inner: [500, 756, 39, 'Lower inner layer'],
    south_core: [500, 668, 37, 'Lower core'],
    west_outer: [174, 500, 41, 'Left outer layer'],
    west_inner: [244, 500, 39, 'Left inner layer'],
    west_core: [332, 500, 37, 'Left core'],
    north_west_outer: [276, 276, 39, 'Paternal upper line'],
    north_west_inner: [352, 352, 37, 'Paternal inner line'],
    north_east_outer: [724, 276, 39, 'Maternal upper line'],
    north_east_inner: [648, 352, 37, 'Maternal inner line'],
    south_east_outer: [724, 724, 39, 'Maternal lower line'],
    south_east_inner: [648, 648, 37, 'Maternal inner line'],
    south_west_outer: [276, 724, 39, 'Paternal lower line'],
    south_west_inner: [352, 648, 37, 'Paternal inner line']
  };

  const reduce22 = (...values) => {
    let result = values.reduce((total, value) => total + Number(value), 0);
    while (result > 22) result = String(result).split('').reduce((total, digit) => total + Number(digit), 0);
    return result;
  };

  const age = (day, month, year) => {
    const today = new Date();
    let value = today.getFullYear() - year;
    if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) value -= 1;
    return value;
  };

  const buildPurposes = nodes => {
    const {west,north,east,south,center,north_west,north_east,south_east,south_west} = nodes;
    const personal_sky = reduce22(north, south), personal_earth = reduce22(west, east);
    const personal = reduce22(personal_sky, personal_earth);
    const social_father = reduce22(north_west, south_east), social_mother = reduce22(north_east, south_west);
    const social = reduce22(social_father, social_mother), spiritual = reduce22(personal, social);
    const planetary = reduce22(social, spiritual), bridge = reduce22(north_west,north_east,south_east,south_west);
    return {
      personal_sky:[north,south,personal_sky], personal_earth:[west,east,personal_earth], personal:[personal],
      social_father:[north_west,south_east,social_father], social_mother:[north_east,south_west,social_mother], social:[social],
      spiritual:[personal,social,spiritual], planetary:[social,spiritual,planetary], power:[personal,bridge,reduce22(personal,bridge)]
    };
  };

  const buildHealth = nodes => {
    const source = [
      ['Sahasrara','Mission',nodes.west,nodes.north], ['Ajna','Vision',nodes.west_outer,nodes.north_outer],
      ['Vishuddha','Expression',nodes.west_inner,nodes.north_inner], ['Anahata','Relationships',nodes.west_core,nodes.north_core],
      ['Manipura','Agency',nodes.center,nodes.center], ['Svadhisthana','Joy',nodes.east_inner,nodes.south_inner],
      ['Muladhara','Grounding',nodes.east,nodes.south]
    ];
    const rows = source.map(([name,theme,body,energy])=>({name,theme,body,energy,emotion:reduce22(body,energy)}));
    rows.push({name:'Total',theme:'Whole field',body:reduce22(...rows.map(r=>r.body)),energy:reduce22(...rows.map(r=>r.energy)),emotion:reduce22(...rows.map(r=>r.emotion))});
    return rows;
  };

  const segmentValues = (start,end) => {
    const middle=reduce22(start,end), first=reduce22(start,middle), second=reduce22(middle,end);
    return [start,reduce22(start,first),first,reduce22(first,middle),middle,reduce22(middle,second),second,reduce22(second,end)];
  };

  const buildAgeCycles = nodes => {
    const anchors=[nodes.west,nodes.north_west,nodes.north,nodes.north_east,nodes.east,nodes.south_east,nodes.south,nodes.south_west,nodes.west];
    const cycles=[];
    for(let decade=0;decade<8;decade++) segmentValues(anchors[decade],anchors[decade+1]).forEach((energy,index)=>cycles.push({age:decade*10+index*1.25,energy}));
    cycles.push({age:80,energy:anchors[8]});
    return cycles;
  };

  const calculate = (name, day, month, year, mode='personal') => {
    const west = reduce22(day);
    const north = reduce22(month);
    const east = reduce22(...String(year).split('').map(Number));
    const south = reduce22(west, north, east);
    const center = reduce22(west, north, east, south);
    const north_west = reduce22(west, north);
    const north_east = reduce22(north, east);
    const south_east = reduce22(east, south);
    const south_west = reduce22(south, west);
    const west_inner = reduce22(center, west), west_outer = reduce22(west_inner, west);
    const north_inner = reduce22(center, north), north_outer = reduce22(north_inner, north);
    const east_inner = reduce22(center, east), east_outer = reduce22(east_inner, east);
    const south_inner = reduce22(center, south), south_outer = reduce22(south_inner, south);
    const nodes = {
      west, north_west, north, north_east, east, south_east, south, south_west, center,
      west_outer, west_inner, west_core: reduce22(west_inner, center),
      north_outer, north_inner, north_core: reduce22(north_inner, center),
      east_outer, east_inner, east_core: reduce22(east_inner, center),
      south_outer, south_inner, south_core: reduce22(south_inner, center),
      north_west_outer: reduce22(west_outer, north_outer),
      north_west_inner: reduce22(west_inner, north_inner),
      north_east_outer: reduce22(north_outer, east_outer),
      north_east_inner: reduce22(north_inner, east_inner),
      south_east_outer: reduce22(east_outer, south_outer),
      south_east_inner: reduce22(east_inner, south_inner),
      south_west_outer: reduce22(south_outer, west_outer),
      south_west_inner: reduce22(south_inner, west_inner)
    };
    return {
      name: name.trim() || 'Your', birth_date: `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`,
      age: age(day, month, year), nodes, mode,
      purposes: buildPurposes(nodes), health:buildHealth(nodes), age_cycles:buildAgeCycles(nodes)
    };
  };

  const calculateCompatibility = (first,second) => {
    const one=calculate(first.name,first.day,first.month,first.year), two=calculate(second.name,second.day,second.month,second.year);
    const nodes=Object.fromEntries(Object.keys(one.nodes).map(key=>[key,reduce22(one.nodes[key],two.nodes[key])]));
    return {name:`${one.name} & ${two.name}`,birth_date:one.birth_date,secondary_birth_date:two.birth_date,age:Math.min(one.age,two.age),nodes,mode:'compatibility',purposes:buildPurposes(nodes),health:null,age_cycles:buildAgeCycles(nodes)};
  };

  const formatDate = value => {
    const [year, month, day] = value.split('-');
    return `${day} · ${month} · ${year}`;
  };

  const setText = (selector, value) => root.querySelectorAll(selector).forEach(el => { el.textContent = value; });

  const selectNode = (key, value, element) => {
    root.querySelectorAll('.dm-node').forEach(node => node.setAttribute('aria-pressed', String(node === element)));
    const [title, theme, copy] = meanings[value];
    setText('[data-insight-number]', value);
    setText('[data-insight-position]', positions[key][3]);
    setText('[data-insight-title]', `${title} · ${theme}`);
    setText('[data-insight-copy]', copy);
  };

  const renderNodes = nodes => {
    const layer = root.querySelector('[data-node-layer]');
    layer.replaceChildren();
    Object.entries(positions).forEach(([key, [x, y, radius]], index) => {
      const value = nodes[key];
      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      const primary = ['north','north_east','east','south_east','south','south_west','west','north_west'].includes(key);
      group.setAttribute('class', `dm-node${primary ? ' dm-node--primary' : ''}${key === 'center' ? ' dm-node--center' : ''}`);
      group.setAttribute('tabindex', '0');
      group.setAttribute('role', 'button');
      group.setAttribute('aria-label', `${positions[key][3]}: ${value}`);
      group.setAttribute('aria-pressed', 'false');
      group.style.setProperty('--node-order', index);
      const halo = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      halo.setAttribute('class', 'dm-node__halo');
      halo.setAttribute('cx', x); halo.setAttribute('cy', y); halo.setAttribute('r', radius + 7);
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('class', 'dm-node__core');
      circle.setAttribute('cx', x); circle.setAttribute('cy', y); circle.setAttribute('r', radius);
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', x); text.setAttribute('y', y + 2); text.textContent = value;
      group.append(halo, circle, text);
      group.addEventListener('click', () => selectNode(key, value, group));
      group.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); selectNode(key, value, group); }
      });
      layer.append(group);
    });
  };

  const renderPurpose = purposes => {
    Object.entries(purposes).forEach(([key, values]) => {
      const target = root.querySelector(`[data-purpose="${key}"]`);
      if (!target) return;
      target.replaceChildren(...values.map(value => {
        const chip = document.createElement('span'); chip.className = 'dm-purpose-chip'; chip.textContent = value; return chip;
      }));
    });
  };

  const sectionSets = {
    personal: [
      ['Personal qualities','north_core','The qualities that become clearer when you act from self-awareness.'],
      ['Purpose','north','The direction that can make your effort feel coherent and meaningful.'],
      ['Money','east_inner','How value, exchange, and practical decisions tend to ask for your attention.'],
      ['Important question','center','The recurring question that can turn an automatic pattern into a conscious choice.'],
      ['Programs','south_core','A familiar response that may repeat until you meet it in a different way.'],
      ['Sexuality','south_east_inner','The conditions that support honesty, trust, and embodied connection.'],
      ['Past life','south','A symbolic look at what may feel instinctive and what can be updated now.'],
      ['Children','south_west_inner','The way care, play, teaching, and responsibility may meet in your life.'],
      ['Relationships','east_core','How you create closeness while keeping mutual respect and clear boundaries.'],
      ['Parents','west_core','What your early environment may have taught you about safety and belonging.'],
      ['Health','north_inner','A symbolic energy overview only; it is not a medical diagnosis or medical advice.'],
      ['Life guidance','north_east_inner','A grounded way to move with this pattern rather than against it.'],
      ['Talents','north_west_inner','A capacity that grows through practice and can become genuinely useful to others.']
    ],
    compatibility: [
      ['Why you met','north','The shared lesson or direction that brings this connection into focus.'],
      ['How the pair expresses itself','east_core','What other people may notice first about the relationship.'],
      ['Financial dynamic','east_inner','How the pair approaches value, resources, decisions, and shared responsibility.'],
      ['Possible friction','south_core','Where different needs can create tension if they remain unnamed.'],
      ['Under stress','south','The automatic pattern the pair may enter when trust or clarity is low.'],
      ['What matters most','north_east_inner','The principle that helps both people return to respect and honest dialogue.'],
      ['Comfort zone','center','What makes the relationship feel familiar, restorative, and emotionally recognizable.']
    ],
    child: [
      ['Personal qualities','north_core','Natural qualities adults can notice and support without forcing a role.'],
      ['Relationship with parents','west_core','What helps guidance feel safe, clear, and respectful for this child.'],
      ['Talents','north_west_inner','A capacity that may grow through curiosity, repetition, and encouragement.'],
      ['Self-realization','east_inner','The environments in which initiative and confidence may develop most naturally.'],
      ['Purpose','north','A symbolic direction for growth, not a demand the child must fulfil.'],
      ['Past pattern','south','A familiar emotional response that benefits from patience and reassurance.'],
      ['Fears and blocks','south_core','Where pressure can close the child down, and what may help restore trust.']
    ]
  };

  const renderAgeCycles = data => {
    const track = root.querySelector('[data-age-cycle]');
    track.replaceChildren();
    const cycles = data.age_cycles || [];
    for (let decade = 0; decade < 8; decade += 1) {
      const points = cycles.slice(decade * 8, decade * 8 + 8);
      const card = document.createElement('article');
      card.className = 'dm-decade';
      const label = document.createElement('div');
      label.className = 'dm-decade__label';
      label.innerHTML = `<span>Chapter</span><strong>${decade * 10}–${(decade + 1) * 10}</strong>`;
      const values = document.createElement('div');
      values.className = 'dm-decade__values';
      points.forEach(point => {
        const value = document.createElement('span');
        value.textContent = point.energy;
        value.title = `Age ${Number(point.age).toFixed(point.age % 1 ? 2 : 0)} · ${meanings[point.energy][0]}`;
        if (Math.abs(point.age - data.age) < .75) value.className = 'is-current';
        values.append(value);
      });
      card.append(label, values);
      track.append(card);
    }
    const nearest = cycles.reduce((best, point) => !best || Math.abs(point.age - data.age) < Math.abs(best.age - data.age) ? point : best, null);
    setText('[data-current-cycle]', nearest ? nearest.energy : '—');
  };

  const renderHealth = data => {
    const section = root.querySelector('[data-health-section]');
    const body = root.querySelector('[data-health-body]');
    section.hidden = !data.health;
    body.replaceChildren();
    (data.health || []).forEach(row => {
      const tr = document.createElement('tr');
      [row.name,row.theme,row.body,row.energy,row.emotion].forEach(value => {
        const td = document.createElement('td'); td.textContent = value; tr.append(td);
      });
      body.append(tr);
    });
  };

  const renderReadingSections = data => {
    const list = root.querySelector('[data-reading-sections]');
    list.replaceChildren();
    const sections = sectionSets[data.mode] || sectionSets.personal;
    const modeTitle = data.mode === 'compatibility' ? 'Your compatibility reading' : data.mode === 'child' ? 'The child matrix reading' : 'Your personal matrix reading';
    const modeIntro = data.mode === 'compatibility'
      ? 'Read the pair as a third system created by two people — not as a verdict on either person.'
      : data.mode === 'child'
        ? 'Use these themes as prompts for observation and support, never as labels that limit a child.'
        : 'Explore each area separately, then return to the center to understand how the themes connect.';
    setText('[data-reading-title]', modeTitle);
    setText('[data-reading-intro]', modeIntro);
    sections.forEach(([title,key,context], index) => {
      const code = data.nodes[key];
      const [archetype, theme, meaning] = meanings[code];
      const item = document.createElement('article');
      item.className = 'dm-reading-item';
      const button = document.createElement('button');
      const bodyId = `dm-reading-${data.mode}-${index}`;
      button.type = 'button'; button.setAttribute('aria-expanded', String(index === 0)); button.setAttribute('aria-controls', bodyId);
      button.innerHTML = `<span>${String(index + 1).padStart(2,'0')}</span><strong>${title}</strong><i aria-hidden="true">+</i>`;
      const body = document.createElement('div');
      body.className = 'dm-reading-item__body'; body.id = bodyId; body.hidden = index !== 0;
      const number = document.createElement('div'); number.className = 'dm-reading-item__code'; number.textContent = code;
      const copy = document.createElement('div');
      copy.innerHTML = `<h4>${archetype} · ${theme}</h4><p>${context} In this position, energy ${code} adds this lens: ${meaning} Notice how this shows up in real choices before deciding what it means for you.</p>`;
      body.append(number, copy); item.append(button, body); list.append(item);
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded)); body.hidden = expanded;
      });
    });
  };

  const setPurposeLabels = mode => {
    const headings = root.querySelectorAll('.dm-purpose-section h3');
    const labels = root.querySelectorAll('.dm-purpose-row > span, .dm-purpose-total > span');
    if (mode === 'compatibility') {
      if (headings[0]) headings[0].textContent = 'Shared direction';
      if (headings[1]) headings[1].textContent = 'Relationship field';
      ['Inner axis','Outer axis','Together','First lineage','Second lineage','Together','Shared lesson','Wider purpose','Key of connection'].forEach((text,index)=>{if(labels[index])labels[index].textContent=text;});
    } else {
      if (headings[0]) headings[0].textContent = 'Personal purpose';
      if (headings[1]) headings[1].textContent = 'Social purpose';
      ['Sky','Earth','Together','Father line','Mother line','Together','Spiritual','Planetary','Key of power'].forEach((text,index)=>{if(labels[index])labels[index].textContent=text;});
    }
  };

  const render = data => {
    const readable = data.secondary_birth_date ? `${formatDate(data.birth_date)} + ${formatDate(data.secondary_birth_date)}` : formatDate(data.birth_date);
    setText('[data-matrix-name]', data.name);
    setText('[data-profile-name]', data.name);
    setText('[data-summary-name]', data.name);
    setText('[data-result-kind]', data.mode === 'compatibility' ? ' · Compatibility Matrix' : data.mode === 'child' ? '’s Child Matrix' : '’s Matrix');
    setText('[data-profile-date]', readable);
    setText('[data-summary-date]', readable);
    setText('[data-profile-age]', data.age);
    renderNodes(data.nodes);
    renderPurpose(data.purposes);
    setPurposeLabels(data.mode);
    renderAgeCycles(data);
    renderReadingSections(data);
    renderHealth(data);
    const centerNode = root.querySelector('.dm-node--center');
    selectNode('center', data.nodes.center, centerNode);
  };

  const isValidDate = (day, month, year) => {
    const candidate = new Date(year, month - 1, day);
    const today = new Date();
    return year >= 1900 && candidate.getFullYear() === year && candidate.getMonth() === month - 1 && candidate.getDate() === day && candidate <= today;
  };

  const form = root.querySelector('[data-matrix-form]');
  const error = root.querySelector('[data-matrix-error]');
  let currentMode = 'personal';
  root.querySelectorAll('[data-matrix-mode]').forEach(tab => {
    tab.addEventListener('click', () => {
      currentMode = tab.dataset.matrixMode;
      root.querySelectorAll('[data-matrix-mode]').forEach(button => button.setAttribute('aria-selected', String(button === tab)));
      root.querySelectorAll('[data-mode-panel]').forEach(panel => { panel.hidden = panel.dataset.modePanel !== currentMode; });
      const label = currentMode === 'compatibility' ? 'Reveal compatibility' : currentMode === 'child' ? 'Reveal child matrix' : 'Reveal my matrix';
      root.querySelector('.dm-submit span').textContent = label;
      error.textContent = '';
    });
  });

  const readDate = (values, prefix='') => ({
    name:String(values.get(`${prefix}name`) || ''), day:Number(values.get(`${prefix}day`)),
    month:Number(values.get(`${prefix}month`)), year:Number(values.get(`${prefix}year`))
  });

  form.addEventListener('submit', event => {
    event.preventDefault();
    const values = new FormData(form);
    let data;
    if (currentMode === 'compatibility') {
      const first=readDate(values,'first_'), second=readDate(values,'second_');
      if (!isValidDate(first.day,first.month,first.year) || !isValidDate(second.day,second.month,second.year)) { error.textContent='Please enter two valid birth dates.'; return; }
      data=calculateCompatibility(first,second);
    } else if (currentMode === 'child') {
      const child=readDate(values,'child_');
      if (!isValidDate(child.day,child.month,child.year)) { error.textContent='Please enter a valid birth date.'; return; }
      data=calculate(child.name,child.day,child.month,child.year,'child');
    } else {
      const person=readDate(values);
      if (!isValidDate(person.day,person.month,person.year)) { error.textContent='Please enter a valid birth date.'; return; }
      data=calculate(person.name,person.day,person.month,person.year,'personal');
    }
    error.textContent = '';
    render(data);
    root.querySelector('[data-matrix-result]').scrollIntoView({behavior: 'smooth', block: 'start'});
  });

  form.querySelectorAll('.dm-date').forEach(dateGroup => {
    const numericFields = [...dateGroup.querySelectorAll('input')];
    numericFields.forEach((field, index) => {
      field.addEventListener('input', () => {
        field.value = field.value.replace(/\D/g, '').slice(0, Number(field.maxLength));
        if (field.value.length === Number(field.maxLength) && numericFields[index + 1]) numericFields[index + 1].focus();
      });
      field.addEventListener('keydown', event => {
        if (event.key === 'Backspace' && !field.value && numericFields[index - 1]) numericFields[index - 1].focus();
      });
    });
  });

  root.querySelector('[data-matrix-restart]').addEventListener('click', () => {
    form.scrollIntoView({behavior: 'smooth', block: 'center'});
    const panel=form.querySelector(`[data-mode-panel="${currentMode}"]`);
    panel.querySelector('input').focus({preventScroll: true});
  });

  const initial = JSON.parse(document.querySelector('#dm-initial-data').textContent);
  render(initial);
})();
